/**
 * 飞书（Feishu / Lark）登录工具
 */

/**
 * 判断是否处于飞书客户端内（UA 包含 Lark / Feishu）
 * @returns {boolean}
 */
export function isFeishuClient() {
  const ua = (window.navigator.userAgent || '').toLowerCase()
  return ua.indexOf('lark') !== -1 || ua.indexOf('feishu') !== -1
}

/**
 * 动态加载飞书 H5 JS-SDK（window.tt）
 * @param {string} url SDK 地址
 * @param {number} timeout 超时时间（毫秒）
 * @returns {Promise}
 */
export function loadFeishuH5Sdk(url, timeout = 8000) {
  return new Promise((resolve, reject) => {
    if (window.tt && window.tt.requestAuthCode) {
      return resolve()
    }
    if (!url) {
      return reject(new Error('飞书H5 SDK地址未配置'))
    }
    const script = document.createElement('script')
    const timer = setTimeout(() => reject(new Error('飞书H5 SDK加载超时')), timeout)
    script.onload = () => {
      clearTimeout(timer)
      if (window.tt && window.tt.requestAuthCode) {
        resolve()
      } else {
        reject(new Error('飞书H5 SDK不可用'))
      }
    }
    script.onerror = () => {
      clearTimeout(timer)
      reject(new Error('飞书H5 SDK加载失败'))
    }
    document.head.appendChild(script)
  })
}

/**
 * 飞书客户端内静默获取免登授权码
 * @param {string} appId 飞书应用 App ID
 * @param {string} sdkUrl 飞书 H5 JS-SDK 地址
 * @returns {Promise<string>} 授权码 code
 */
export function getFeishuH5AuthCode(appId, sdkUrl) {
  return loadFeishuH5Sdk(sdkUrl).then(() => new Promise((resolve, reject) => {
    window.tt.requestAuthCode({
      appId,
      success: (res) => resolve((res && res.code) || ''),
      fail: (e) => reject(new Error((e && (e.errorMessage || e.errorMsg)) || '获取飞书授权码失败'))
    })
  }))
}

/**
 * 构造飞书授权登录跳转地址（state 固定为 feishu，用于回调时区分钉钉/飞书）
 * @param {object} feishuApp 后端返回的飞书应用配置 {app_id, redirect_uri, scope, authorize_url, h5_sdk_url}
 * @param {string} redirectUri 授权回调地址
 * @param {string} state 状态值（固定 feishu）
 * @returns {string}
 */
export function buildFeishuAuthorizeUrl(feishuApp, redirectUri, state = 'feishu') {
  let url = `${feishuApp.authorize_url}?client_id=${feishuApp.app_id}` +
    `&redirect_uri=${redirectUri}` +
    `&response_type=code&state=${state}`
  if (feishuApp.scope) {
    url += `&scope=${encodeURIComponent(feishuApp.scope)}`
  }
  return url
}

/**
 * 获取飞书登录回调地址
 * @param {string} [path] 指定回调路径（如 '/home'）；不传或非 / 开头时使用当前页面路径
 * @returns {string}
 */
export function getFeishuRedirectUri(path) {
  let p = path
  if (typeof p !== 'string' || !p.startsWith('/')) {
    p = window.location.pathname
  }
  let uri = `${window.location.origin}${p}`
  return encodeURIComponent(uri)
}
